"use client";

import React, { useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useLocalization } from "@/providers/useLocalization";
import { useTranslations } from "@/providers/TranslationsProvider";
import type { ApplicationDocument, UserApplication } from "@/lib/userApplication";
import { MIN_APPLICANT_AGE, isOldEnough } from "@/lib/age";
import { DEFAULT_COUNTRY_BY_LANGUAGE, licenceTypesFor, validateLicence } from "@/lib/licence";
import type { SpecialtyEntry } from "@/services/usersService";
import type { LanguageKey } from "@/providers/LocalizationProvider";
import Button from "@/components/Button";
import PersonalStep from "./PersonalStep";
import SpecialtiesStep from "./SpecialtiesStep";
import LicenceStep from "./LicenceStep";
import ProfileStep from "./ProfileStep";
import ReviewStep from "./ReviewStep";
import StatusCard from "./StatusCard";
import { APPLICATION_ANCHOR } from "./JoinLanding";
import type { FieldErrors, WizardDraft } from "./types";
const REVIEW_STEP = 4;

/** Each step's `?step=` value, so a step survives a reload, a shared link and the browser's back button. */
const STEP_SLUGS = ["personal", "specialties", "licence", "profile", "review"] as const;

/** Which step owns a field the API reports on a failed submit. */
function stepOf(field: string): number {
  if (field === "profile.specialties" || field === "profile.approaches") return 1;
  if (field.startsWith("licence.")) return 2;
  if (field.startsWith("profile.")) return 3;
  return 0;
}
function toDraft(application: UserApplication, language: LanguageKey): WizardDraft {
  const {
    personal,
    profile
  } = application;
  // Not chosen yet (or saved empty): the country that goes with the UI language.
  const country = application.licence?.country || DEFAULT_COUNTRY_BY_LANGUAGE[language];
  const types = licenceTypesFor(country);
  return {
    first_name: personal.first_name ?? "",
    last_name: personal.last_name ?? "",
    gender: personal.gender ?? "",
    birthdate: personal.birthdate ?? "",
    specialties: profile.specialties ?? [],
    approaches: profile.approaches ?? [],
    licence: {
      ...application.licence,
      country,
      type: application.licence?.type ?? (types.length === 1 ? types[0].id : null)
    },
    headline: profile.headline ?? "",
    bio: profile.bio ?? "",
    languages: profile.languages ?? [language],
    years_of_experience: profile.years_of_experience == null ? "" : String(profile.years_of_experience)
  };
}
function payloadFor(step: number, draft: WizardDraft): Record<string, unknown> {
  switch (step) {
    case 0:
      return {
        first_name: draft.first_name.trim(),
        last_name: draft.last_name.trim(),
        gender: draft.gender || null,
        birthdate: draft.birthdate || null
      };
    case 1:
      return {
        profile: {
          specialties: draft.specialties,
          approaches: draft.approaches
        }
      };
    case 2:
      return {
        licence: draft.licence
      };
    case 3:
      return {
        profile: {
          languages: draft.languages,
          headline: draft.headline.trim() || null,
          bio: draft.bio.trim() || null,
          years_of_experience: draft.years_of_experience === "" ? null : Number(draft.years_of_experience)
        }
      };
    default:
      return {};
  }
}
interface DoctorRegistrationProps {
  application: UserApplication;
  catalogue: SpecialtyEntry[];
}

/**
 * Doctor registration wizard: about you → specialties → licence → public
 * profile → review. Every "Next" saves the step as a draft, so a doctor can
 * leave and pick up where they stopped; submit sends it for admin review.
 * The step lives in the URL (?step=licence); a link can't skip past the first
 * incomplete step.
 */
export default function DoctorRegistration({
  application,
  catalogue
}: Readonly<DoctorRegistrationProps>) {
  const {
    t
  } = useTranslations();
  const {
    language
  } = useLocalization();
  const top = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const urlStep = useSearchParams().get("step");
  const [status, setStatus] = useState(application.status);
  const [rejectionReason] = useState(application.rejection_reason);
  const [submittedAt, setSubmittedAt] = useState(application.submitted_at);
  // A rejected application opened on a step (reload, back button) is being edited.
  const [editing, setEditing] = useState(application.status === "rejected" && !!urlStep);
  const [draft, setDraft] = useState(() => toDraft(application, language));
  const [documents, setDocuments] = useState<ApplicationDocument[]>(application.documents);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const stepLabels = [t("join.step personal", "About you"), t("join.step specialties", "Specialties"), t("join.step licence", "Licence"), t("join.step profile", "Profile"), t("join.step review", "Review")];
  const required = t("join.required", "This field is required");
  const invalid = t("join.invalid", "Please check this value");
  const tooYoung = t("join.too young", "You must be at least {{age}} years old").replace("{{age}}", String(MIN_APPLICANT_AGE));
  const serverFieldMessage = (field: string) => {
    if (field === "profile.specialties" || field === "profile.languages") return required;
    return field === "birthdate" ? tooYoung : invalid;
  };
  const update = (patch: Partial<WizardDraft>) => {
    setDraft(d => ({
      ...d,
      ...patch
    }));
    setErrors({});
  };
  const signInAgain = () => {
    const next = encodeURIComponent(`/${language}/join`);
    window.location.href = `/${language}?auth=phone&next=${next}`;
  };
  const validators: Record<number, () => FieldErrors> = {
    0: () => ({
      ...(draft.first_name.trim() ? {} : {
        first_name: required
      }),
      ...(draft.last_name.trim() ? {} : {
        last_name: required
      }),
      ...(draft.gender ? {} : {
        gender: required
      }),
      ...(!draft.birthdate || isOldEnough(draft.birthdate) ? {} : {
        birthdate: tooYoung
      })
    }),
    1: () => draft.specialties.length ? {} : {
      "profile.specialties": t("join.pick specialty", "Pick at least one specialty")
    },
    2: () => Object.fromEntries(validateLicence(draft.licence).map(field_0 => [`licence.${field_0}`, field_0 === "country" || field_0 === "type" ? required : invalid])),
    3: () => draft.languages.length ? {} : {
      "profile.languages": t("join.pick language", "Pick at least one language")
    }
  };
  const validate = (current: number): FieldErrors => validators[current]?.() ?? {};

  /** The URL's step, held back at the first incomplete one before it. */
  const stepFromUrl = (slug: string | null): number => {
    const requested = Math.max(0, STEP_SLUGS.indexOf(slug as (typeof STEP_SLUGS)[number]));
    for (let i = 0; i < requested; i++) {
      if (Object.keys(validate(i)).length) return i;
    }
    return requested;
  };
  const [step, setStep] = useState(() => stepFromUrl(urlStep));
  // Follow the URL when it changes (back/forward, or our own push landing).
  // Compared with the last URL seen, not the step we asked for, so the render
  // before a push lands doesn't snap back.
  const [seenUrlStep, setSeenUrlStep] = useState(urlStep);
  if (urlStep !== seenUrlStep) {
    setSeenUrlStep(urlStep);
    setStep(stepFromUrl(urlStep));
  }
  const goTo = (next_0: number) => {
    setStep(next_0);
    router.push(`${pathname}?step=${STEP_SLUGS[next_0]}`, {
      scroll: false
    });
    setFailure("");
    top.current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  /** Saves a step; false when the wizard can't continue. */
  async function save(current_0: number): Promise<boolean> {
    const res = await fetch("/api/v1/users/application", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json"
      },
      // This wizard is the doctor application.
      body: JSON.stringify({
        type: "doctor",
        ...payloadFor(current_0, draft)
      })
    });
    if (res.status === 401) {
      signInAgain();
      return false;
    }
    if (res.status === 409) {
      // Submitted or approved from another tab - show the real state.
      window.location.reload();
      return false;
    }
    return res.ok;
  }

  /** Validates and saves the current step, then opens `target` (the next one by default). */
  async function next_1(target = step + 1) {
    const errs = validate(step);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setBusy(true);
    try {
      if (await save(step)) {
        goTo(target);
      } else {
        setFailure(t("join.save failed", "We couldn't save your progress. Please try again."));
      }
    } catch {
      setFailure(t("join.save failed", "We couldn't save your progress. Please try again."));
    } finally {
      setBusy(false);
    }
  }
  async function submit() {
    setBusy(true);
    setFailure("");
    try {
      const res_0 = await fetch("/api/v1/users/application/submit", {
        method: "POST"
      });
      if (res_0.status === 401) {
        signInAgain();
        return;
      }
      const data: {
        success?: boolean;
        fields?: string[];
        return?: Partial<UserApplication>;
      } = await res_0.json().catch(() => ({}));
      if (data.success) {
        setStatus("pending_review");
        setSubmittedAt(data.return?.submitted_at ?? new Date().toISOString());
        setEditing(false);
        // The status card has no steps.
        router.replace(pathname, {
          scroll: false
        });
        top.current?.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
        return;
      }
      if (data.fields?.length) {
        const errs_0: FieldErrors = {};
        for (const field_1 of data.fields) {
          errs_0[field_1] = serverFieldMessage(field_1);
        }
        setErrors(errs_0);
        goTo(Math.min(...data.fields.map(stepOf)));
        return;
      }
      setFailure(t("join.submit failed", "We couldn't submit your application. Please try again."));
    } catch {
      setFailure(t("join.submit failed", "We couldn't submit your application. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  // A step in the progress bar opens when it's behind the current one, or
  // ahead with everything before it complete (e.g. after going back to fix
  // something). Never an unfinished one.
  const canOpen = (i_0: number) => i_0 < step || i_0 > step && stepFromUrl(STEP_SLUGS[i_0]) === i_0;
  const openStep = (i_1: number) => {
    if (busy || !canOpen(i_1)) return;
    if (i_1 < step) goTo(i_1);else next_1(i_1);
  };
  const showStatus = status === "pending_review" || status === "approved" || status === "rejected" && !editing;
  return <div ref={top} id={APPLICATION_ANCHOR} className="scroll-mt-28">
      <header className="mb-6 sm:mb-8 text-center">
        <h1 className="text-[22px] sm:text-[32px] font-semibold leading-[1.125] tracking-[0.004em] text-[var(--foreground)]">
          {t("join.title", "Join {{app}} as a doctor")}
        </h1>
      </header>

      {showStatus ? <StatusCard status={status as "pending_review" | "approved" | "rejected"} rejectionReason={rejectionReason} submittedAt={submittedAt} reviewedAt={status === "pending_review" ? null : application.reviewed_at} onEdit={() => {
      setEditing(true);
      goTo(0);
    }} /> : <div className="rounded-[2rem] bg-white dark:bg-[#1C1C1E] ring-1 ring-black/[0.05] dark:ring-white/[0.06] p-5 sm:p-10 shadow-[0_24px_48px_-24px_rgba(0,0,0,0.15)]">
          <nav aria-label={t("labels.step of", "Step {{current}} of {{total}}").replace("{{current}}", String(step + 1)).replace("{{total}}", String(stepLabels.length))} className="mb-6 sm:mb-8">
            <ol className="flex gap-2">
              {stepLabels.map((label, i_2) => <li key={label} className="flex-1 min-w-0">
                  <button type="button" onClick={() => openStep(i_2)} disabled={i_2 !== step && !canOpen(i_2)} aria-current={i_2 === step ? "step" : undefined} aria-label={label} className="group block w-full py-2 -my-2 text-start enabled:cursor-pointer disabled:cursor-default">
                    <div className={`h-1 rounded-full transition-colors duration-300 ${i_2 <= step ? "bg-[#0071E3]" : "bg-[#E8E8ED] dark:bg-[#3A3A3C]"} ${i_2 === step ? "" : "group-enabled:group-hover:opacity-70"}`} />
                    <span className={`mt-1.5 sm:mt-2 block text-[11px] sm:text-[12px] leading-tight hyphens-auto sm:truncate ${i_2 === step ? "text-[var(--foreground)]" : "text-[#86868B] group-enabled:group-hover:text-[#0071E3]"}`}>
                      {label}
                    </span>
                  </button>
                </li>)}
            </ol>
          </nav>

          {step === 0 && <PersonalStep draft={draft} update={update} errors={errors} />}
          {step === 1 && <SpecialtiesStep draft={draft} update={update} errors={errors} catalogue={catalogue} />}
          {step === 2 && <LicenceStep draft={draft} update={update} errors={errors} documents={documents} onDocumentsChange={setDocuments} onUnauthorized={signInAgain} />}
          {step === 3 && <ProfileStep draft={draft} update={update} errors={errors} />}
          {step === REVIEW_STEP && <ReviewStep draft={draft} catalogue={catalogue} documents={documents} confirmed={confirmed} onConfirmedChange={setConfirmed} onEdit={goTo} />}

          {failure && <p role="alert" className="mt-6 text-[14px] text-[#E30000]">{failure}</p>}

          <div className="mt-8 sm:mt-10 pt-5 sm:pt-6 border-t border-[#D2D2D7]/60 dark:border-[#424245]/60 flex items-center justify-between gap-3">
            {step > 0 ? <Button type="button" variant="apple-gray" size="xl" onClick={() => goTo(step - 1)} disabled={busy}>
                <svg className="w-4 h-4 -ms-1 rtl:rotate-180" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                </svg>
                {t("buttons.back", "Back")}
              </Button> : <span />}
            {step < REVIEW_STEP ? <Button type="button" variant="apple" size="xl" loading={busy} onClick={() => next_1()}>
                {t("join.next", "Next")}
              </Button> : <Button type="button" variant="apple" size="xl" loading={busy} disabled={!confirmed} onClick={submit}>
                {t("join.submit", "Submit application")}
              </Button>}
          </div>
        </div>}
    </div>;
}